import type { TranslateTextsPayload, TranslateTextsResponse } from '@/features/campaign-texts/types/campaign-texts.types';

import type { CountryGenResult } from './multiCountryBulkEdit.types';

const CHUNK = 15;

type TranslateFn = (p: TranslateTextsPayload) => Promise<TranslateTextsResponse>;

export async function translateTextsInChunks(texts: string[], mutate: TranslateFn): Promise<string[]> {
  const out: string[] = [];
  for (let i = 0; i < texts.length; i += CHUNK) {
    const { translations } = await mutate({ texts: texts.slice(i, i + CHUNK) });
    out.push(...translations);
  }
  return out;
}

function alignRow(len: number, existing?: (string | null | undefined)[]): string[] {
  return Array.from({ length: len }, (_, i) => {
    const e = existing?.[i];
    return e != null && String(e).trim() ? String(e) : '';
  });
}

/** Fills only empty slots; preserves existing non-empty English lines. */
async function translateMissingInLineArray(
  texts: string[],
  existingRow: (string | null | undefined)[] | undefined,
  mutate: TranslateFn,
): Promise<string[]> {
  const row = alignRow(texts.length, existingRow);
  const missingIdx: number[] = [];
  for (let i = 0; i < texts.length; i++) {
    if (!row[i]?.trim()) missingIdx.push(i);
  }
  if (missingIdx.length === 0) return row;
  const payload = missingIdx.map((i) => texts[i]!);
  const parts = await translateTextsInChunks(payload, mutate);
  missingIdx.forEach((i, j) => {
    row[i] = parts[j] ?? '';
  });
  return row;
}

export type TranslateCountryGenScope = { batchIndex?: number };

/** English preview: only calls the API for lines that still lack a translation. */
export async function translateCountryGenResult(
  result: CountryGenResult,
  mutate: TranslateFn,
  scope?: TranslateCountryGenScope,
): Promise<Pick<CountryGenResult, 'translations' | 'translationBatches'>> {
  const batches = result.textBatches;
  if (batches?.length) {
    const trb = batches.map((b, i) => alignRow(b.length, result.translationBatches?.[i]));
    for (let bi = 0; bi < batches.length; bi++) {
      if (scope?.batchIndex != null && bi !== scope.batchIndex) continue;
      const texts = batches[bi] ?? [];
      if (texts.length === 0) continue;
      trb[bi] = await translateMissingInLineArray(texts, trb[bi], mutate);
    }
    return { translationBatches: trb, translations: trb.flat() };
  }
  if (scope?.batchIndex != null && scope.batchIndex !== 0) return {};
  if (result.texts.length === 0) return {};
  const translations = await translateMissingInLineArray(result.texts, result.translations, mutate);
  return { translations };
}

export function countryGenResultHasTexts(r: CountryGenResult): boolean {
  if (r.texts.length > 0) return true;
  return Boolean(r.textBatches?.some((b) => b.length > 0));
}

/** True if at least one SMS line in the batch still lacks a non-empty English line. */
export function batchNeedsEnglishTranslation(r: CountryGenResult, batchIdx: number): boolean {
  if (r.textBatches?.length) {
    const batch = r.textBatches[batchIdx] ?? [];
    const tr = r.translationBatches?.[batchIdx];
    if (!batch.length) return false;
    if (!tr || tr.length !== batch.length) return true;
    return tr.some((t) => !t?.trim());
  }
  if (batchIdx !== 0) return false;
  if (!r.texts.length) return false;
  const tr = r.translations;
  if (!tr || tr.length !== r.texts.length) return true;
  return tr.some((t) => !t?.trim());
}

export function countryGenResultFullyTranslated(r: CountryGenResult): boolean {
  const batches = r.textBatches;
  if (batches?.some((b) => b.length > 0)) {
    const trans = r.translationBatches ?? [];
    for (let i = 0; i < batches.length; i++) {
      const texts = batches[i] ?? [];
      if (texts.length === 0) continue;
      const tr = trans[i];
      if (!tr || tr.length !== texts.length) return false;
      if (tr.some((t) => !t?.trim())) return false;
    }
    return batches.some((b) => b.length > 0);
  }
  if (r.texts.length === 0) return true;
  const tr = r.translations;
  if (!tr || tr.length !== r.texts.length) return false;
  return tr.every((t) => Boolean(t?.trim()));
}
